import { useEffect, useMemo, useState, Suspense } from 'react';
import { Layout, Menu, Avatar, Dropdown, Tag, MenuProps, message, Spin } from 'antd';
import { useQuery } from '@tanstack/react-query';
import { tenantApi } from '../api/tenant';
import {
  DashboardOutlined,
  ProfileOutlined,
  CustomerServiceOutlined,
  TeamOutlined,
  BankOutlined,
  AuditOutlined,
  BgColorsOutlined,
  FileImageOutlined,
  ShoppingOutlined,
  DollarOutlined,
  FileTextOutlined,
  FormOutlined,
  SafetyOutlined,
  FileSearchOutlined,
  NotificationOutlined,
  DownloadOutlined,
  LogoutOutlined,
  MessageOutlined,
  ApartmentOutlined,
  IdcardOutlined,
  ClusterOutlined,
  BarChartOutlined,
  KeyOutlined,
  LockOutlined,
  DownOutlined,
  TrophyOutlined,
  AppstoreOutlined,
} from '@ant-design/icons';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { useTenantAuth, TenantInfo } from '../stores/auth.tenant';
import { useGeoContext } from '../hooks/useGeoContext';

const { Sider, Content, Header } = Layout;

type MenuItem = Required<MenuProps>['items'][number];

// 「名片审核」菜单 label — 带待审核数量红圆角标（>0 才显示；>99 显示 99+）。
function cardReviewLabel(pendingCount: number) {
  if (!pendingCount || pendingCount <= 0) return '名片审核';
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
      名片审核
      <span
        style={{
          background: '#ff4d4f',
          color: '#fff',
          borderRadius: 10,
          minWidth: 18,
          height: 18,
          lineHeight: '18px',
          fontSize: 12,
          padding: '0 5px',
          textAlign: 'center',
          display: 'inline-block',
          boxSizing: 'border-box',
        }}
      >
        {pendingCount > 99 ? '99+' : pendingCount}
      </span>
    </span>
  );
}

function buildMenu(tenant: TenantInfo, pendingCardReview = 0, geoOpened = false): MenuItem[] {
  const isGroup = tenant.tenant_type === 'group' && tenant.is_group_root;
  const isSubsidiary = tenant.tenant_type === 'enterprise' && !!tenant.parent_tenant_id;

  // 集团根账号 — 8 个分组（集团专属 + 企业通用功能）
  if (isGroup) {
    return [
      { key: '/dashboard', icon: <DashboardOutlined />, label: '工作台' },
      { key: '/h3yun-training', icon: <TrophyOutlined />, label: '培训考核' },
      { key: 'g-geo-content', icon: <BarChartOutlined />, label: 'GEO管理', children: [
        { key: '/geo-content/overview', icon: <BarChartOutlined />, label: '数据概览' },
      ] },
      {
        key: 'g-info',
        icon: <ProfileOutlined />,
        label: '集团资料',
        children: [
          { key: '/profile', icon: <ProfileOutlined />, label: '集团信息' },
          { key: '/customer-service', icon: <CustomerServiceOutlined />, label: '在线客服' },
          { key: '/rag', icon: <FileSearchOutlined />, label: '智能客服知识库' },
          { key: '/company-brief', icon: <ProfileOutlined />, label: '集团简介' },
          { key: '/enterprise-shop', icon: <ShoppingOutlined />, label: '商铺权限' },
          // 集团根**刻意不给「企业亮点」入口**：M 端首页那个 2×2 图卡
          // 展位对集团/政府是留给「旗下子公司」的（CompanyShowcase 的 showHlGrid 带 !isGroupLike，
          // ADR-51 V5 互斥网格），集团发布的亮点在 M 端**没有任何渲染路径**——网格不出、
          // 「查看更多」也写在网格内部一并消失。留着入口 = 运营能配能发布能占配额，用户永远看不到。
          // 企业资讯不受影响（showNews 无 isGroupLike 判定），故 /news 保留。
          // V5：新增企业资讯（图文 only）独立栏目，与企业亮点同表分域（category='news'）
          { key: '/news', icon: <FileTextOutlined />, label: '企业资讯' },
          { key: '/announcements', icon: <NotificationOutlined />, label: '公告' },
          { key: '/miniapp-style', icon: <BgColorsOutlined />, label: '小程序风格' }, // 双风格
          { key: '/workbench-tabs', icon: <AppstoreOutlined />, label: '工作台Tab配置' },
        ],
      },
      {
        key: 'g-group',
        icon: <ClusterOutlined />,
        label: '集团管理',
        children: [
          { key: '/group/home', icon: <BankOutlined />, label: '集团总部' },
          { key: '/group/subsidiaries', icon: <BankOutlined />, label: '子公司管理' },
          { key: '/group/seats', icon: <TeamOutlined />, label: '席位分发' },
          { key: '/group/applications', icon: <AuditOutlined />, label: '加入申请' },
        ],
      },
      {
        key: 'g-people',
        icon: <TeamOutlined />,
        label: '员工与人脉',
        children: [
          { key: '/employees', icon: <TeamOutlined />, label: '员工管理' },
          { key: '/org-structure', icon: <ApartmentOutlined />, label: '组织架构' }, // ADR-46
          { key: '/seat-roster', icon: <IdcardOutlined />, label: '席位花名册' },
          { key: '/profiles/pending-review', icon: <FileSearchOutlined />, label: '人脉待确认' },
        ],
      },
      {
        key: 'g-cards',
        icon: <IdcardOutlined />,
        label: '名片管理',
        children: [
          { key: '/cards/pending-review', icon: <AuditOutlined />, label: cardReviewLabel(pendingCardReview) },
          { key: '/form-schema', icon: <FormOutlined />, label: '联系表单' },
        ],
      },
      { key: '/message-board', icon: <MessageOutlined />, label: '留言板' },
      {
        key: 'g-finance',
        icon: <DollarOutlined />,
        label: '财务与订单',
        children: [
          { key: '/subscription', icon: <ShoppingOutlined />, label: '当前套餐' },
          { key: '/orders', icon: <DollarOutlined />, label: '订单' },
          { key: '/invoices', icon: <FileTextOutlined />, label: '发票记录' },
          { key: '/ai-usage', icon: <DollarOutlined />, label: 'AI 用量' },
          { key: '/coin-pool', icon: <DollarOutlined />, label: '企业元力池' }, // ADR-49
        ],
      },
      { key: '/roles', icon: <KeyOutlined />, label: '角色权限' },
      {
        key: 'g-security',
        icon: <SafetyOutlined />,
        label: '安全与日志',
        children: [
          { key: '/login-logs', icon: <SafetyOutlined />, label: '登录日志' },
        ],
      },
    ];
  }

  // personal 租户菜单已删（PM）：E 端个人登录权限已在后端关闭
  // （resolveManagedTenants 排除 personal），个人用户 M 端闭环，本布局不会再收到 personal 会话。

  // 企业租户 — 6-7 个分组
  const groupBinding: MenuItem | null = isSubsidiary
    ? { key: '/group/binding', icon: <BankOutlined />, label: '集团绑定' }
    : tenant.tenant_type === 'enterprise'
      ? { key: '/group/apply', icon: <BankOutlined />, label: '加入集团' }
      : null;

  return [
    { key: '/dashboard', icon: <DashboardOutlined />, label: '工作台' },
    { key: '/h3yun-training', icon: <TrophyOutlined />, label: '培训考核' },
    ...(['enterprise', 'government'].includes(tenant.tenant_type) && geoOpened ? [{ key: 'g-geo-content', icon: <BarChartOutlined />, label: 'GEO管理', children: [
      { key: '/geo-content/overview', icon: <BarChartOutlined />, label: '数据概览' },
      { key: '/geo-content/analysis', icon: <FileSearchOutlined />, label: '数据分析' },
      { key: '/geo-content/publish', icon: <FileTextOutlined />, label: '渠道发布' },
    ] }] : []),
    {
      key: 'g-info',
      icon: <ProfileOutlined />,
      label: '企业资料',
      children: [
        { key: '/profile', icon: <ProfileOutlined />, label: '企业信息' },
        ...(tenant.tenant_type === 'enterprise' ? [{ key: '/enterprise-shop', icon: <ShoppingOutlined />, label: '商铺权限' }] : []),
        { key: '/customer-service', icon: <CustomerServiceOutlined />, label: '在线客服' },
        { key: '/rag', icon: <FileSearchOutlined />, label: '智能客服知识库' },
        { key: '/company-brief', icon: <ProfileOutlined />, label: '公司简介' },
        { key: '/highlights', icon: <FileImageOutlined />, label: '企业亮点' },
          // V5：新增企业资讯（图文 only）独立栏目，与企业亮点同表分域（category='news'）
          { key: '/news', icon: <FileTextOutlined />, label: '企业资讯' },
        { key: '/announcements', icon: <NotificationOutlined />, label: '公告' },
        { key: '/miniapp-style', icon: <BgColorsOutlined />, label: '小程序风格' }, // 双风格
        { key: '/workbench-tabs', icon: <AppstoreOutlined />, label: '工作台Tab配置' },
        ...(groupBinding ? [groupBinding] : []),
      ],
    },
    {
      key: 'g-people',
      icon: <TeamOutlined />,
      label: '员工与人脉',
      children: [
        { key: '/employees', icon: <TeamOutlined />, label: '员工管理' },
        { key: '/org-structure', icon: <ApartmentOutlined />, label: '组织架构' }, // ADR-46
        { key: '/seat-roster', icon: <IdcardOutlined />, label: '席位花名册' },
        { key: '/profiles/pending-review', icon: <FileSearchOutlined />, label: '人脉待确认' },
      ],
    },
    {
      key: 'g-cards',
      icon: <IdcardOutlined />,
      label: '名片管理',
      children: [
        { key: '/cards/pending-review', icon: <AuditOutlined />, label: cardReviewLabel(pendingCardReview) },
        { key: '/form-schema', icon: <FormOutlined />, label: '联系表单' },
      ],
    },
    { key: '/message-board', icon: <MessageOutlined />, label: '留言板' },
    {
      key: 'g-finance',
      icon: <DollarOutlined />,
      label: '财务与订单',
      children: [
        { key: '/subscription', icon: <ShoppingOutlined />, label: '当前套餐' },
        { key: '/orders', icon: <DollarOutlined />, label: '订单' },
        { key: '/invoices', icon: <FileTextOutlined />, label: '发票记录' },
        { key: '/ai-usage', icon: <DollarOutlined />, label: 'AI 用量' },
          { key: '/coin-pool', icon: <DollarOutlined />, label: '企业元力池' }, // ADR-49
      ],
    },
    { key: '/roles', icon: <KeyOutlined />, label: '角色权限' },
    {
      key: 'g-security',
      icon: <SafetyOutlined />,
      label: '安全与日志',
      children: [
        { key: '/login-logs', icon: <SafetyOutlined />, label: '登录日志' },
      ],
    },
    { key: '/exports', icon: <DownloadOutlined />, label: '数据导出' },
  ];
}

const TYPE_LABEL: Record<string, string> = {
  personal: '个人',
  enterprise: '企业',
  group: '集团',
  government: '政府',
};

export default function TenantLayout() {
  const navigate = useNavigate();
  const location = useLocation();
  const isGeoPublishWorkbench = location.pathname.startsWith('/geo-content/publish/');
  const { user, tenant, logout } = useTenantAuth();
  const geoContext = useGeoContext();
  const setAuth = useTenantAuth((s) => s.setAuth);
  const [switching, setSwitching] = useState(false);

  // ADR-34 §2.5 — 当前管理租户切换器（仅当可管理 >1 个租户时显示）
  // 只列 status='active' 的：后端 switch-tenant 对禁用/注销租户是硬拒
  // （`tenant_not_active`），不过滤就会列出一个点了必报错的死条目。
  const { data: managedAll } = useQuery({
    queryKey: ['managed-tenants'],
    queryFn: () => tenantApi.listManagedTenants(),
    staleTime: 5 * 60 * 1000,
    enabled: !!tenant,
  });
  const managed = useMemo(
    () => managedAll?.filter((m) => m.status === 'active'),
    [managedAll],
  );

  // 侧边栏「名片审核」待审核数量角标（个人租户名片免审，无此队列，不查）
  const { data: pendingCards } = useQuery({
    queryKey: ['t-cards-pending-count'],
    queryFn: () => tenantApi.listPendingCards({ page: 1, page_size: 1 }),
    enabled: !!tenant && tenant.tenant_type !== 'personal',
    staleTime: 30 * 1000,
    refetchInterval: 60 * 1000,
  });
  const pendingCardReview = pendingCards?.total ?? 0;

  // 集团根：企业亮点无 M 端渲染路径（见 buildMenu 里同名注释），菜单已摘除，
  // 这里补路由守卫 —— 只摘菜单挡不住手敲 URL / 旧书签 / 历史记录回退，
  // 那些路径进去照样能建能发布，等于洞没堵上（菜单+路由双闭环，与曾经的
  // PERSONAL_DENY_PREFIXES 同思路）。
  // 只拦 /highlights，**不能拦 /news** —— 两者同表分域但企业资讯对集团正常显示。
  //    用 startsWith 覆盖 /highlights/new 与 /highlights/:id/edit。
  const isGroupRoot = tenant?.tenant_type === 'group' && !!tenant?.is_group_root;
  useEffect(() => {
    if (isGroupRoot && location.pathname.startsWith('/highlights')) {
      navigate('/dashboard', { replace: true });
    }
  }, [isGroupRoot, location.pathname, navigate]);

  const doSwitch = async (tid: string) => {
    if (!tenant || !user || tid === tenant.id || switching) return;
    const expectedJwt = useTenantAuth.getState().jwt;
    setSwitching(true);
    try {
      const r = await tenantApi.switchTenant(tid);
      useTenantAuth.getState().commitIfCurrent(expectedJwt, () => setAuth(r.jwt, user, r.tenant));
      message.success(`已切换到 ${r.tenant.name}`);
      window.location.assign('/dashboard'); // 整页重载，按新租户重取所有数据
    } catch {
      message.error('切换失败');
      setSwitching(false);
    }
  };

  const menu = useMemo(() => (tenant ? buildMenu(tenant, pendingCardReview, geoContext.data?.opened === true) : []), [tenant, pendingCardReview, geoContext.data?.opened]);

  // 路径 → 所属分组 key — 按最长 child path 前缀匹配，决定哪一组自动展开
  const groupKeyForPath = useMemo(() => {
    const map = new Map<string, string>();
    for (const g of menu) {
      const group = g as { key?: string; children?: { key?: string }[] };
      if (!group?.children) continue;
      for (const c of group.children) {
        if (c?.key) map.set(c.key, group.key as string);
      }
    }
    let best = '';
    let bestLen = 0;
    for (const [path, gk] of map) {
      if ((location.pathname === path || location.pathname.startsWith(path + '/')) && path.length > bestLen) {
        best = gk;
        bestLen = path.length;
      }
    }
    return best;
  }, [location.pathname, menu]);

  // 手风琴 openKeys —— 同一时间只开一个分组 + 跟随路径自动展开
  const [openKeys, setOpenKeys] = useState<string[]>(groupKeyForPath ? [groupKeyForPath] : []);
  useEffect(() => {
    if (groupKeyForPath) setOpenKeys([groupKeyForPath]);
  }, [groupKeyForPath]);

  const onOpenChange: MenuProps['onOpenChange'] = (keys) => {
    const latest = keys.find((k) => !openKeys.includes(k));
    setOpenKeys(latest ? [latest] : []);
  };

  return (
    <Layout style={{ minHeight: '100vh' }}>
      <Sider width={240}>
        <div style={{ color: '#fff', padding: 20, fontWeight: 600, fontSize: 14 }}>
          {managed && managed.length > 1 ? (
            <Dropdown
              trigger={['click']}
              menu={{
                items: managed.map((m) => ({
                  key: m.tenant_id,
                  label: (
                    <span>
                      {m.name}
                      <Tag style={{ marginLeft: 6 }} color={m.tenant_type === 'personal' ? 'default' : 'blue'}>
                        {TYPE_LABEL[m.tenant_type] || m.tenant_type}
                      </Tag>
                      {m.tenant_id === tenant?.id && <span style={{ color: '#52c41a' }}>✓</span>}
                    </span>
                  ),
                  onClick: () => doSwitch(m.tenant_id),
                })),
              }}
            >
              <span style={{ cursor: 'pointer' }}>
                {tenant?.name || '...'}
                {tenant && <Tag style={{ marginLeft: 8 }} color="blue">{TYPE_LABEL[tenant.tenant_type] || tenant.tenant_type}</Tag>}
                <DownOutlined style={{ fontSize: 10, marginLeft: 4 }} />
              </span>
            </Dropdown>
          ) : (
            <>
              {tenant?.name || '...'}
              {tenant && <Tag style={{ marginLeft: 8 }} color="blue">{TYPE_LABEL[tenant.tenant_type] || tenant.tenant_type}</Tag>}
            </>
          )}
        </div>
        <Menu
          theme="dark"
          mode="inline"
          selectedKeys={[location.pathname]}
          openKeys={openKeys}
          onOpenChange={onOpenChange}
          items={menu}
          onClick={(e) => navigate(e.key)}
        />
      </Sider>
      <Layout>
        {!isGeoPublishWorkbench && <Header style={{ background: '#fff', textAlign: 'right', padding: '0 24px' }}>
          <Dropdown
            menu={{
              items: [
                { key: 'change-password', icon: <LockOutlined />, label: '修改密码', onClick: () => navigate('/change-password') },
                { type: 'divider' },
                { key: 'logout', icon: <LogoutOutlined />, label: '退出', onClick: () => { if (logout()) navigate('/login'); } },
              ],
            }}
          >
            <span style={{ cursor: 'pointer' }}>
              <Avatar style={{ marginRight: 8 }}>{user?.username?.[0]?.toUpperCase() || 'T'}</Avatar>
              {user?.nickname || user?.username || '管理员'}
            </span>
          </Dropdown>
        </Header>}
        <Content style={{ padding: isGeoPublishWorkbench ? 0 : 24 }}>
          {/* 路由级懒加载：各页 React.lazy 动态导入，切页时 Suspense 兜底加载态 */}
          <Suspense fallback={<div style={{ display: 'flex', justifyContent: 'center', padding: 80 }}><Spin size="large" /></div>}>
            <Outlet />
          </Suspense>
        </Content>
      </Layout>
    </Layout>
  );
}
